import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Card,
  CardBody,
  Text,
  Badge,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Link,
  Divider,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface Book {
  id: number;
  name: string;
  price: number;
  classId: number;
  subjectId: number;
  seriesId: number;
  class?: {
    id: number;
    name: string;
  };
  subject?: {
    id: number;
    name: string;
  };
  series?: {
    id: number;
    name: string;
    type?: string;
  };
  bookFiles?: any[];
}

interface FormDataType {
  seriesId: string;
  subjectId: string;
  name: string;
  price: string;
}

const BooksPage = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [series, setSeries] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [bookTypes, setBookTypes] = useState([]);
  const [bookFiles, setBookFiles] = useState<{[key: number]: any[]}>({});
  const [loading, setLoading] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [selectedBookForFiles, setSelectedBookForFiles] = useState<Book | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState<FormDataType>({
    seriesId: '',
    subjectId: '',
    name: '',
    price: '',
  });

  const [fileFormData, setFileFormData] = useState({
    bookId: '',
    typeId: '',
    link: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { isOpen: isBookModalOpen, onOpen: onBookModalOpen, onClose: onBookModalClose } = useDisclosure();
  const { isOpen: isFileModalOpen, onOpen: onFileModalOpen, onClose: onFileModalClose } = useDisclosure();
  const { isOpen: isViewFilesModalOpen, onOpen: onViewFilesModalOpen, onClose: onViewFilesModalClose } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Filter books based on search term
  const filteredBooks = useMemo(() => {
    if (!searchTerm) return books;
    const searchTermLower = searchTerm.toLowerCase();
    
    return books.filter((book) =>
      book.name.toLowerCase().includes(searchTermLower) ||
      book.price.toString().includes(searchTermLower) ||
      (book.class?.name && book.class.name.toLowerCase().includes(searchTermLower)) ||
      (book.subject?.name && book.subject.name.toLowerCase().includes(searchTermLower)) ||
      (book.series?.name && book.series.name.toLowerCase().includes(searchTermLower))
    );
  }, [books, searchTerm]);

  // Filter subjects based on selected series
  const filteredSubjects = useMemo(() => {
    if (!formData.seriesId) return [];
    return subjects.filter((subject: any) => subject.seriesId === Number(formData.seriesId));
  }, [subjects, formData.seriesId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [booksData, classesData, subjectsData, seriesData, bookTypesData] = await Promise.all([
        api.getBooks(),
        api.getClasses(),
        api.getSubjects(),
        api.getSeries(),
        api.getBookTypes(),
      ]);
      setBooks(booksData);
      setClasses(classesData);
      setSubjects(subjectsData);
      setSeries(seriesData);
      setBookTypes(bookTypesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBookFiles = async (bookId: number) => {
    try {
      const files = await api.getBookFilesByBookId(bookId);
      setBookFiles(prev => ({ ...prev, [bookId]: files }));
      return files;
    } catch (error) {
      console.error('Error loading book files:', error);
      return [];
    }
  };

  const handleBookSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    if (!formData.name.trim() || !formData.price || !formData.subjectId) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const selectedSubject = subjects.find((s: any) => s.id === Number(formData.subjectId));
      const bookData = {
        name: formData.name.trim(),
        subjectId: Number(formData.subjectId),
        price: Number(formData.price),
      };

      if (editingBook) {
        await api.updateBook(editingBook.id, bookData);
        toast({
          title: 'Success',
          description: 'Book updated successfully',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.createBook(bookData);
        toast({
          title: 'Success',
          description: 'Book created successfully',
          status: 'success',
          duration: 3000,
        });
      }
      
      resetBookForm();
      onBookModalClose();
      loadData();
    } catch (error: any) {
      console.error('Error saving book:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save book',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile && !fileFormData.link) {
      toast({
        title: 'Error',
        description: 'Please select a file or provide a link',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const uploadData = new FormData();
      uploadData.append('bookId', fileFormData.bookId);
      uploadData.append('typeId', fileFormData.typeId);
      
      if (selectedFile) {
        uploadData.append('file', selectedFile);
      }
      
      if (fileFormData.link) {
        uploadData.append('link', fileFormData.link);
      }

      await api.uploadFile(uploadData);
      toast({
        title: 'Success',
        description: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
      });
      
      resetFileForm();
      onFileModalClose();
      loadData();
      
      // Refresh files for the selected book if viewing files modal is open
      if (selectedBookForFiles) {
        await loadBookFiles(selectedBookForFiles.id);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload file',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBook = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this book?')) {
      try {
        await api.deleteBook(id);
        toast({
          title: 'Success',
          description: 'Book deleted successfully',
          status: 'success',
          duration: 3000,
        });
        loadData();
      } catch (error) {
        console.error('Error deleting book:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete book',
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const handleDeleteBookFile = async (fileId: number) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await api.deleteBookFile(fileId);
        toast({
          title: 'Success',
          description: 'File deleted successfully',
          status: 'success',
          duration: 2000,
        });
        
        // Refresh files for the current book
        if (selectedBookForFiles) {
          await loadBookFiles(selectedBookForFiles.id);
        }
        loadData(); // Refresh main data to update file counts
      } catch (error) {
        console.error('Error deleting file:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete file',
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const handleEditBook = (book: Book) => {
    setEditingBook(book);
    setFormData({
      seriesId: book.seriesId.toString(),
      subjectId: book.subjectId.toString(),
      name: book.name,
      price: book.price.toString(),
    });
    onBookModalOpen();
  };

  const resetBookForm = () => {
    setFormData({ seriesId: '', subjectId: '', name: '', price: '' });
    setEditingBook(null);
  };

  const resetFileForm = () => {
    setFileFormData({ bookId: '', typeId: '', link: '' });
    setSelectedFile(null);
  };

  const handleViewFiles = async (book: Book) => {
    setSelectedBookForFiles(book);
    await loadBookFiles(book.id);
    onViewFilesModalOpen();
  };

  const handleAddFileToBook = (book: Book) => {
    setFileFormData({ ...fileFormData, bookId: book.id.toString() });
    onFileModalOpen();
  };

  const handleCreateSubject = () => {
    onBookModalClose();
    navigate('/subjects');
  };

  const handleCreateClass = () => {
    onBookModalClose();
    navigate('/classes');
  };

  const getFileUrl = (file: any) => {
    if (file.link) return file.link;
    if (file.basePath) return `${file.baseUrl || 'http://localhost:3000'}/${file.basePath}`;
    return null;
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: 'Success',
        description: 'Link copied to clipboard',
        status: 'success',
        duration: 2000,
      });
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl" color="gray.700">
          📕 Books Management
        </Heading>
        <HStack>
          <Button colorScheme="orange" onClick={() => { resetBookForm(); onBookModalOpen(); }}>
            ➕ Add Book
          </Button>
          <Button colorScheme="cyan" onClick={() => { resetFileForm(); onFileModalOpen(); }}>
            📁 Upload File
          </Button>
        </HStack>
      </HStack>

      {/* Search Bar */}
      <Card bg={cardBg} shadow="sm">
        <CardBody>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Text>🔍</Text>
            </InputLeftElement>
            <Input
              placeholder="Search books by name, price, class, subject, or series..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
            />
          </InputGroup>
          {searchTerm && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Found {filteredBooks.length} of {books.length} books
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Books Table */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          {loading ? (
            <VStack py={8}>
              <Spinner size="xl" />
              <Text>Loading...</Text>
            </VStack>
          ) : filteredBooks.length === 0 ? (
            searchTerm ? (
              <Alert status="info">
                <AlertIcon />
                No books found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No books found. Create your first book!
              </Alert>
            )
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Book Name</Th>
                  <Th>Price</Th>
                  <Th>Class</Th>
                  <Th>Subject</Th>
                  <Th>Series</Th>
                  <Th>Files</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredBooks.map((book) => {
                  const subject = subjects.find((s: any) => s.id === book.subjectId);
                  const subjectClass = classes.find((c: any) => c.id === subject?.classId);
                  const subjectSeries = series.find((s: any) => s.id === subject?.seriesId);
                  
                  return (
                    <Tr key={book.id}>
                      <Td fontWeight="medium">{book.name}</Td>
                      <Td>
                        <Badge colorScheme="green">${book.price}</Badge>
                      </Td>
                      <Td>{subjectClass?.name || 'N/A'}</Td>
                      <Td>{subject?.name || 'N/A'}</Td>
                      <Td>
                        <Badge colorScheme="purple">
                          {subjectSeries?.name || 'N/A'}
                        </Badge>
                      </Td>
                      <Td>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          colorScheme="blue"
                          onClick={() => handleViewFiles(book)}
                        >
                          📁 {book.bookFiles?.length || 0} files
                        </Button>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            icon={<Text>✏️</Text>}
                            aria-label="Edit"
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleEditBook(book)}
                            isDisabled={loading}
                          />
                          <IconButton
                            icon={<Text>🗑️</Text>}
                            aria-label="Delete"
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDeleteBook(book.id)}
                            isDisabled={loading}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Book Modal */}
      <Modal isOpen={isBookModalOpen} onClose={onBookModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingBook ? 'Edit Book' : 'Create New Book'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleBookSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Series</FormLabel>
                  <Select
                    value={formData.seriesId}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        seriesId: e.target.value,
                        subjectId: ''
                      });
                    }}
                    isDisabled={loading}
                  >
                    <option value="">Choose a series</option>
                    {series.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {item.name} {item.type ? `(${item.type})` : ''}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Subject</FormLabel>
                  <Select
                    value={formData.subjectId}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        subjectId: e.target.value
                      });
                    }}
                    isDisabled={!formData.seriesId || loading}
                  >
                    <option value="">Choose a subject</option>
                    {filteredSubjects.map((item: any) => {
                      const subjectClass = classes.find((c: any) => c.id === item.classId);
                      return (
                        <option key={item.id} value={item.id}>
                          {item.name} ({subjectClass?.name || 'No Class'})
                        </option>
                      );
                    })}
                  </Select>
                  {!formData.seriesId && (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Please select a series first
                    </Text>
                  )}
                  {formData.seriesId && filteredSubjects.length === 0 && (
                    <Alert status="info" mt={2} size="sm">
                      <AlertIcon />
                      <Box>
                        <Text>No subjects found in this series.</Text>
                        <Link color="blue.500" onClick={handleCreateSubject} cursor="pointer">
                          Click here to create a subject
                        </Link>
                      </Box>
                    </Alert>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Book Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Algebra Fundamentals"
                    isDisabled={loading}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Price</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="29.99"
                    isDisabled={loading}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onBookModalClose} isDisabled={loading}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="orange" isLoading={loading}>
                {editingBook ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* File Upload Modal */}
      <Modal isOpen={isFileModalOpen} onClose={onFileModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload File to Book</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleFileSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Book</FormLabel>
                  <Select
                    value={fileFormData.bookId}
                    onChange={(e) => setFileFormData({ ...fileFormData, bookId: e.target.value })}
                  >
                    <option value="">Choose a book</option>
                    {books.map((book) => (
                      <option key={book.id} value={book.id}>
                        {book.name} - {book.subject?.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>File Type</FormLabel>
                  <Select
                    value={fileFormData.typeId}
                    onChange={(e) => setFileFormData({ ...fileFormData, typeId: e.target.value })}
                  >
                    <option value="">Choose file type</option>
                    {bookTypes.map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.typeName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Upload File</FormLabel>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    accept=".pdf,.doc,.docx,.epub,.mp3,.mp4"
                    p={1}
                  />
                  {selectedFile && (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Selected: {selectedFile.name}
                    </Text>
                  )}
                </FormControl>
                <Text textAlign="center">OR</Text>
                <FormControl>
                  <FormLabel>External Link</FormLabel>
                  <Input
                    value={fileFormData.link}
                    onChange={(e) => setFileFormData({ ...fileFormData, link: e.target.value })}
                    placeholder="https://example.com/file.pdf"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onFileModalClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="cyan" isLoading={loading}>
                Upload
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* View Files Modal */}
      <Modal isOpen={isViewFilesModalOpen} onClose={onViewFilesModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="start" spacing={2}>
              <HStack>
                <Text>📁 Files for "{selectedBookForFiles?.name}"</Text>
              </HStack>
              <HStack spacing={2} fontSize="sm">
                <Badge colorScheme="blue">{selectedBookForFiles?.subject?.name}</Badge>
                <Badge colorScheme="purple">{selectedBookForFiles?.series?.name}</Badge>
                <Badge colorScheme="orange">Class: {selectedBookForFiles?.class?.name}</Badge>
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="medium">
                  Total Files: {bookFiles[selectedBookForFiles?.id || 0]?.length || 0}
                </Text>
                <Button
                  size="sm"
                  colorScheme="cyan"
                  onClick={() => selectedBookForFiles && handleAddFileToBook(selectedBookForFiles)}
                >
                  ➕ Add File
                </Button>
              </HStack>
              
              <Divider />
              
              {!bookFiles[selectedBookForFiles?.id || 0] || bookFiles[selectedBookForFiles?.id || 0]?.length === 0 ? (
                <Alert status="info">
                  <AlertIcon />
                  No files uploaded for this book yet.
                </Alert>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>File Type</Th>
                      <Th>File/Link</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {bookFiles[selectedBookForFiles?.id || 0]?.map((file: any) => {
                      const fileUrl = getFileUrl(file);
                      return (
                        <Tr key={file.id}>
                          <Td>
                            <Badge colorScheme="teal">
                              {file.bookType?.typeName}
                            </Badge>
                          </Td>
                          <Td maxW="300px">
                            <Text fontSize="sm" isTruncated>
                              {file.link ? '🔗' : '📄'} {file.link || file.basePath}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              {fileUrl && (
                                <>
                                  <Tooltip label="Copy Link">
                                    <IconButton
                                      icon={<Text>📋</Text>}
                                      aria-label="Copy link"
                                      size="xs"
                                      colorScheme="blue"
                                      variant="ghost"
                                      onClick={() => handleCopyLink(fileUrl)}
                                    />
                                  </Tooltip>
                                  <Tooltip label="Open in New Tab">
                                    <IconButton
                                      icon={<Text>🔗</Text>}
                                      aria-label="Open link"
                                      size="xs"
                                      colorScheme="green"
                                      variant="ghost"
                                      onClick={() => window.open(fileUrl, '_blank')}
                                    />
                                  </Tooltip>
                                  {file.basePath && (
                                    <Tooltip label="Download File">
                                      <IconButton
                                        icon={<Text>⬇️</Text>}
                                        aria-label="Download file"
                                        size="xs"
                                        colorScheme="purple"
                                        variant="ghost"
                                        as="a"
                                        href={fileUrl}
                                        download
                                        target="_blank"
                                      />
                                    </Tooltip>
                                  )}
                                </>
                              )}
                              <Tooltip label="Delete File">
                                <IconButton
                                  icon={<Text>🗑️</Text>}
                                  aria-label="Delete file"
                                  size="xs"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleDeleteBookFile(file.id)}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewFilesModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default BooksPage; 